import { schoolSubjectsApi } from '@/services/schoolSubjectsApi'

const state = () => ({
  subjects: [],
  selectedSubject: null,
  pagination: {
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 15
  },
  loading: false,
  error: null
})

const getters = {
  allSubjects: (state) => state.subjects,
  activeSubjects: (state) => state.subjects.filter(subject => subject.is_active),
  inactiveSubjects: (state) => state.subjects.filter(subject => !subject.is_active),
  isLoading: (state) => state.loading,
  hasError: (state) => state.error,
  getSelectedSubject: (state) => state.selectedSubject,
  getPagination: (state) => state.pagination
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },

  SET_ERROR(state, error) {
    state.error = error
  },

  SET_SUBJECTS(state, subjects) {
    state.subjects = subjects
  },

  SET_PAGINATION(state, pagination) {
    state.pagination = {
      currentPage: pagination.current_page || pagination.currentPage || 1,
      lastPage: pagination.last_page || pagination.lastPage || 1,
      total: pagination.total || 0,
      perPage: pagination.per_page || pagination.perPage || 15
    }
  },

  SET_SELECTED_SUBJECT(state, subject) {
    state.selectedSubject = subject
  },

  ADD_SUBJECT(state, subject) {
    state.subjects.unshift(subject)
  },

  UPDATE_SUBJECT(state, updatedSubject) {
    const index = state.subjects.findIndex(subject => subject.id === updatedSubject.id)
    if (index !== -1) {
      state.subjects.splice(index, 1, updatedSubject)
    }
  },

  REMOVE_SUBJECT(state, subjectId) {
    state.subjects = state.subjects.filter(subject => subject.id !== subjectId)
  },

  CLEAR_ERROR(state) {
    state.error = null
  },

  CLEAR_SELECTED_SUBJECT(state) {
    state.selectedSubject = null
  }
}

const actions = {
  async fetchSubjects({ commit }, params = {}) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await schoolSubjectsApi.getSchoolSubjects(params)

      // Handle both direct response and nested data structure
      const responseData = response.data || response
      const subjectsData = responseData.data || responseData

      commit('SET_SUBJECTS', Array.isArray(subjectsData) ? subjectsData : [])

      // Set pagination if available
      if (responseData.meta || responseData.current_page) {
        const paginationData = responseData.meta || responseData
        commit('SET_PAGINATION', paginationData)
      }

      return { success: true, data: responseData }
    } catch (error) {
      let errorMessage = 'Error al cargar las asignaturas'

      if (error.status === 403) {
        errorMessage = 'No tienes permisos para realizar esta acción.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchAllSubjects({ commit }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      let allSubjects = []
      let currentPage = 1
      let lastPage = 1

      // Fetch all pages
      do {
        const response = await schoolSubjectsApi.getSchoolSubjects({ page: currentPage })

        // El interceptor de axios ya devuelve response.data
        // Entonces response = { data: [...], links: {...}, meta: {...} }
        const subjectsData = response.data
        const meta = response.meta

        // Agregar las asignaturas de esta página
        if (Array.isArray(subjectsData)) {
          allSubjects = [...allSubjects, ...subjectsData]
        }

        // Actualizar lastPage desde meta
        if (meta && meta.last_page) {
          lastPage = meta.last_page
        }

        console.log(`Página ${currentPage} de ${lastPage} - Asignaturas cargadas: ${allSubjects.length}`)

        currentPage++
      } while (currentPage <= lastPage)

      console.log(`Total de asignaturas cargadas: ${allSubjects.length}`)
      commit('SET_SUBJECTS', allSubjects)

      return { success: true, data: allSubjects }
    } catch (error) {
      let errorMessage = 'Error al cargar las asignaturas'

      if (error.status === 403) {
        errorMessage = 'No tienes permisos para realizar esta acción.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchSubject({ commit }, subjectId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await schoolSubjectsApi.getSchoolSubject(subjectId)
      const subjectData = response.data || response
      commit('SET_SELECTED_SUBJECT', subjectData)

      return { success: true, data: subjectData }
    } catch (error) {
      let errorMessage = 'Error al cargar la asignatura'

      if (error.status === 403) {
        errorMessage = 'No tienes permisos para realizar esta acción.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createSubject({ commit }, subjectData) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await schoolSubjectsApi.createSchoolSubject(subjectData)
      const createdSubject = response.data || response
      commit('ADD_SUBJECT', createdSubject)

      return { success: true, data: createdSubject, message: 'Asignatura creada exitosamente' }
    } catch (error) {
      let errorMessage = 'Error al crear la asignatura'

      if (error.status === 403) {
        errorMessage = 'No tienes permisos para realizar esta acción.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateSubject({ commit, state }, { id, subjectData }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      const response = await schoolSubjectsApi.updateSchoolSubject(id, subjectData)
      const updatedSubject = response.data || response
      commit('UPDATE_SUBJECT', updatedSubject)

      // Update selected subject if it's the one being edited
      if (state.selectedSubject && state.selectedSubject.id === id) {
        commit('SET_SELECTED_SUBJECT', updatedSubject)
      }

      return { success: true, data: updatedSubject, message: 'Asignatura actualizada exitosamente' }
    } catch (error) {
      let errorMessage = 'Error al actualizar la asignatura'

      if (error.status === 403) {
        errorMessage = 'No tienes permisos para realizar esta acción.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteSubject({ commit }, subjectId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')

    try {
      await schoolSubjectsApi.deleteSchoolSubject(subjectId)
      commit('REMOVE_SUBJECT', subjectId)

      return { success: true, message: 'Asignatura eliminada exitosamente' }
    } catch (error) {
      let errorMessage = 'Error al eliminar la asignatura'

      if (error.status === 403) {
        errorMessage = 'No tienes permisos para realizar esta acción.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  setSelectedSubject({ commit }, subject) {
    commit('SET_SELECTED_SUBJECT', subject)
  },

  clearSelectedSubject({ commit }) {
    commit('CLEAR_SELECTED_SUBJECT')
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
